@if (loading()) {
  <section
    class="score-strip score-strip--loading"
    role="status"
    aria-label="Carregando o score de crédito">
    <span class="skeleton skeleton--ring"></span>
    <span class="skeleton skeleton--wide"></span>
    <span class="skeleton"></span>
    <span class="skeleton skeleton--short"></span>
  </section>
} @else if (failed()) {
  <section class="score-strip score-strip--error" role="alert">
    <i class="pi pi-exclamation-triangle"></i>
    <span class="score-strip__message"
      >Não foi possível calcular o score agora. Os demonstrativos abaixo continuam válidos.</span
    >
    <app-button
      class="score-strip__action"
      label="Tentar de novo"
      theme="white"
      size="small"
      (sendButtonClick)="retry()" />
  </section>
} @else if (!hasScore()) {
  <section class="score-strip">
    <div class="score-strip__alert-icon"><i class="pi pi-exclamation-circle"></i></div>
    <div class="score-strip__message">
      <p class="score-strip__title">Score ainda não disponível</p>
      <p class="score-strip__text">{{ insufficientMessage() }}</p>
    </div>
    <app-button
      class="score-strip__action"
      label="Ver o que falta"
      theme="white"
      size="small"
      (sendButtonClick)="drawerOpen.set(true)" />
  </section>
} @else {
  <section class="score-strip">
    <div class="score-strip__main">
      <div class="score-ring" [style.--p]="ringPct()">
        <div class="score-ring__inner">{{ score() }}</div>
      </div>
      <div>
        <p class="score-strip__caption">
          Score de crédito · {{ year() }}
          @if (partial()) {
            <span
              class="score-strip__tag"
              title="A nota usa os números até o último balancete e pode mudar no fechamento do ano."
              >exercício em curso</span
            >
          }
        </p>
        <p class="score-strip__value">{{ score() }} <span>de 1000</span></p>
      </div>
    </div>

    <div class="score-strip__item">
      <p class="score-strip__caption">Rating equivalente</p>
      <p class="score-strip__strong">
        {{ rating().sp }} <span>· Moody's {{ rating().moodys }}</span>
      </p>
    </div>
    <div class="score-strip__item">
      <p class="score-strip__caption">Cobertura dos indicadores</p>
      <p class="score-strip__strong">{{ coverageLabel() }}</p>
    </div>
    @if (variation(); as change) {
      <div class="score-strip__item">
        <p class="score-strip__caption">vs. {{ change.year }}</p>
        <p class="score-strip__strong" [attr.data-tone]="change.tone">{{ change.text }}</p>
      </div>
    }
    <div class="score-strip__item score-strip__item--wide">
      <p class="score-strip__caption">Próximo rating</p>
      <p class="score-strip__text">{{ nextRatingLabel() }}</p>
    </div>

    <app-button
      class="score-strip__action"
      icon="pi pi-window-maximize"
      label="Ver composição"
      theme="white"
      size="small"
      (sendButtonClick)="drawerOpen.set(true)" />
  </section>
}

<!-- `p-drawer` direto: não há wrapper Vault de painel lateral. baseZIndex 10300 (faixa das modais,
     src/styles/Z-INDEX.md) e appendTo body, como no drawer de comentários da jornada bancária. -->
<p-drawer
  [visible]="drawerOpen()"
  (visibleChange)="drawerOpen.set($event)"
  position="right"
  [modal]="true"
  [dismissible]="true"
  [closeOnEscape]="true"
  [blockScroll]="true"
  [baseZIndex]="10300"
  appendTo="body"
  [style]="{ width: '440px', maxWidth: '100vw' }"
  [header]="year() ? 'Composição do score · ' + year() : 'Composição do score'">
  @if (company(); as current) {
    <div class="score-drawer">
      <p class="score-drawer__intro">
        700 pontos de indicadores gerais e 300 do segmento {{ current.segment }}.
        @if (!hasScore()) {
          O score aparece quando houver indicadores suficientes; os que estão "sem dado" são os que
          faltam.
        }
      </p>

      <table class="score-drawer__table">
        <thead>
          <tr>
            <th>Indicador</th>
            <th class="num">Valor</th>
            <th class="num">Nota</th>
          </tr>
        </thead>
        <tbody>
          @for (row of generalRows(); track row.key) {
            <tr>
              <td>
                {{ row.label }}
                @if (row.estimated) {
                  <span class="score-drawer__tag">estimado</span>
                }
              </td>
              <td class="num">{{ row.value }}</td>
              <td class="num" [class.muted]="row.missing">{{ row.score }}</td>
            </tr>
          }
          @if (sectorRows().length) {
            <tr class="score-drawer__group">
              <td colspan="3">Indicadores do segmento</td>
            </tr>
            @for (row of sectorRows(); track row.key) {
              <tr>
                <td>{{ row.label }}</td>
                <td class="num">{{ row.value }}</td>
                <td class="num" [class.muted]="row.missing">{{ row.score }}</td>
              </tr>
            }
          }
        </tbody>
      </table>

      @if (historyBars().length) {
        <h4 class="score-drawer__subtitle">Histórico</h4>
        <div class="score-drawer__bars">
          @for (bar of historyBars(); track bar.year) {
            <div class="score-drawer__bar-col">
              <div
                class="score-drawer__bar"
                [class.current]="bar.current"
                [style.height.%]="bar.heightPct"
                [title]="bar.year + ': ' + bar.score + ' pontos'"></div>
            </div>
          }
        </div>
        <div class="score-drawer__bar-labels">
          @for (bar of historyBars(); track bar.year) {
            <span>{{ bar.year }} · {{ bar.score }}</span>
          }
        </div>
      }

      <p class="score-drawer__footer">
        Calculado com os demonstrativos validados desta tela. Rating equivalente é uma referência
        interna do Cognito, não uma nota emitida pelas agências.
      </p>
    </div>
  }
</p-drawer>
